import type { TranslationPhrase } from '@src/types.ts/translation';

/**
 * Keep only the phrases that are still live (PLAT-10945).
 *
 * A correction never deletes the phrases it replaces. It RETIRES them —
 * `active: false` plus `supersededBy` pointing at the override that took over
 * those words — and adds the override's own live phrase. The retired records
 * stay in `TranslationData.phrases[]` because they are what makes the override
 * reversible, so every reader has to decide for itself what it is looking at.
 *
 * The predicate is `active !== false`, NOT `active === true` and not a
 * truthiness test. The backend's `TranslationPhrase`
 * (`arbor-v1/lib/translation/types.ts`) has no `active` field at all, so every
 * machine-generated phrase arrives without one; treating an absent flag as
 * "not live" would discard the entire phrase list of any translation that has
 * never been corrected. This matches what the field's own documentation states
 * (`src/types.ts/translation.ts`) and what the transcript views, the review
 * workspace, the coverage cache and the SRT export already do.
 *
 * Returns `null` unchanged: the caption producer reads `null` as "no
 * translation for this language" and falls back to original-language
 * segmentation, which is a different outcome from an empty list.
 */
export function selectLivePhrases(
  phrases: readonly TranslationPhrase[] | null,
): readonly TranslationPhrase[] | null {
  if (!phrases) return null;
  return phrases.filter(p => p.active !== false);
}
